/**
 * The three run modes. Each is opt in and independent: randomizer and chaos
 * both fold into the overrides object the next send builds (use-chat.ts),
 * and endless mode drives its own headless machine (use-endless.ts) rather
 * than touching the chat transcript at all. Turning all three off reproduces
 * today's plain request, which is the whole determinism contract.
 */

import type { ReactElement } from 'react';

import type { ScenarioInfo } from '../../hooks/use-chat.ts';
import type { EndlessController } from '../../hooks/use-endless.ts';
import type { SettingsController } from '../../hooks/use-settings.ts';

/** The fault types applyOverrides knows how to inject; see overrides.ts makeFault. */
const CHAOS_FAULT_TYPES = [
  'destroy_socket',
  'stall',
  'duplicate_last',
  'reorder',
  'malformed_json',
  'evict_ring',
  'split_utf8',
  'drop_mid_frame',
  'rate_limit_resume',
] as const;

function randomSeed(): number {
  return Math.floor(Math.random() * 1_000_000_000);
}

export function ModePanel({
  settingsCtl,
  endless,
}: {
  settingsCtl: SettingsController;
  scenarios: ScenarioInfo[];
  endless: EndlessController;
}): ReactElement {
  const { settings, update } = settingsCtl;

  return (
    <section className="settings__section" aria-labelledby="settings-modes-heading">
      <h2 id="settings-modes-heading">Run modes</h2>

      <div className="panel settings__mode">
        <div className="settings__mode-heading">
          <label className="switch">
            <input
              type="checkbox"
              checked={settings.randomizer.enabled}
              onChange={(event) => {
                update((prev) => ({
                  ...prev,
                  randomizer: { ...prev.randomizer, enabled: event.target.checked },
                }));
              }}
            />
            <span className="switch__track">
              <span className="switch__thumb" />
            </span>
            <span className="switch__label">
              <strong>Randomizer</strong>
            </span>
          </label>
          <p className="muted small">
            Draws every knob for the selected scenario from its declared range, seeded so the same
            seed reproduces the same run.
          </p>
        </div>
        <div className="settings__mode-controls">
          <label className="knob" htmlFor="randomizer-seed">
            <span className="knob__label">Seed</span>
            <div className="knob__control">
              <input
                id="randomizer-seed"
                type="number"
                className="field settings__seed-input"
                value={settings.randomizer.seed}
                onChange={(event) => {
                  const seed = Number(event.target.value);
                  update((prev) => ({ ...prev, randomizer: { ...prev.randomizer, seed } }));
                }}
              />
              <button
                type="button"
                className="button button--quiet"
                onClick={() => {
                  update((prev) => ({
                    ...prev,
                    randomizer: { ...prev.randomizer, seed: randomSeed() },
                  }));
                }}
              >
                Reroll seed
              </button>
            </div>
          </label>
        </div>
      </div>

      <div className="panel settings__mode">
        <div className="settings__mode-heading">
          <label className="switch">
            <input
              type="checkbox"
              checked={settings.chaos.enabled}
              onChange={(event) => {
                update((prev) => ({
                  ...prev,
                  chaos: { ...prev.chaos, enabled: event.target.checked },
                }));
              }}
            />
            <span className="switch__track">
              <span className="switch__thumb" />
            </span>
            <span className="switch__label">
              <strong>Chaos</strong>
            </span>
          </label>
          <p className="muted small">
            Seeded random fault injection into whichever scenario is selected, distinct from the
            fixed <code>chaos</code> scenario, which stays as the deterministic demo.
          </p>
        </div>
        <div className="settings__mode-controls">
          <label className="knob" htmlFor="chaos-intensity">
            <span className="knob__label">Intensity</span>
            <div className="knob__control">
              <input
                id="chaos-intensity"
                type="range"
                className="range"
                min={0}
                max={1}
                step={0.05}
                value={settings.chaos.intensity}
                onChange={(event) => {
                  const intensity = Number(event.target.value);
                  update((prev) => ({ ...prev, chaos: { ...prev.chaos, intensity } }));
                }}
              />
              <span className="mono knob__value">{settings.chaos.intensity.toFixed(2)}</span>
            </div>
          </label>
          <label className="knob" htmlFor="chaos-seed">
            <span className="knob__label">Seed</span>
            <div className="knob__control">
              <input
                id="chaos-seed"
                type="number"
                className="field settings__seed-input"
                value={settings.chaos.seed}
                onChange={(event) => {
                  const seed = Number(event.target.value);
                  update((prev) => ({ ...prev, chaos: { ...prev.chaos, seed } }));
                }}
              />
              <button
                type="button"
                className="button button--quiet"
                onClick={() => {
                  update((prev) => ({ ...prev, chaos: { ...prev.chaos, seed: randomSeed() } }));
                }}
              >
                Reroll seed
              </button>
            </div>
          </label>
          <fieldset className="settings__fault-picker">
            <legend className="muted small">Fault types</legend>
            {CHAOS_FAULT_TYPES.map((fault) => {
              const checked = settings.chaos.faults.includes(fault);
              return (
                <label key={fault} className="settings__fault-option">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      update((prev) => {
                        const faults = event.target.checked
                          ? [...prev.chaos.faults, fault]
                          : prev.chaos.faults.filter((f) => f !== fault);
                        return { ...prev, chaos: { ...prev.chaos, faults } };
                      });
                    }}
                  />
                  <span className="mono">{fault}</span>
                </label>
              );
            })}
          </fieldset>
        </div>
      </div>

      <div className="panel settings__mode">
        <div className="settings__mode-heading">
          <label className="switch">
            <input
              type="checkbox"
              checked={settings.endless.enabled}
              onChange={(event) => {
                update((prev) => ({
                  ...prev,
                  endless: { ...prev.endless, enabled: event.target.checked },
                }));
              }}
            />
            <span className="switch__track">
              <span className="switch__thumb" />
            </span>
            <span className="switch__label">
              <strong>Endless</strong>
            </span>
          </label>
          <p className="muted small">
            Cycles the whole catalogue forever, checking the client's own shipped invariants after
            each run. Stops on tab navigation away from here, or on a manual send in Chat.
          </p>
        </div>
        <div className="settings__mode-controls">
          <label className="knob" htmlFor="endless-delay">
            <span className="knob__label">Delay between runs</span>
            <div className="knob__control">
              <input
                id="endless-delay"
                type="range"
                className="range"
                min={0}
                max={10_000}
                step={100}
                value={settings.endless.delayMs}
                onChange={(event) => {
                  const delayMs = Number(event.target.value);
                  update((prev) => ({ ...prev, endless: { ...prev.endless, delayMs } }));
                }}
              />
              <span className="mono knob__value">{settings.endless.delayMs}ms</span>
            </div>
          </label>

          {endless.running || endless.results.length > 0 ? (
            <div className="settings__endless-tally">
              <span className="badge badge--signal">{endless.running ? 'running' : 'stopped'}</span>
              <span className="mono">
                pass <strong className="mono">{endless.pass}</strong>
              </span>
              <span className="mono">
                fail <strong className="mono">{endless.fail}</strong>
              </span>
              {endless.current !== null && (
                <span className="muted mono">now: {endless.current}</span>
              )}
            </div>
          ) : (
            <p className="muted small">
              No runs yet. Turn endless on to start cycling the catalogue.
            </p>
          )}

          {endless.results.length > 0 && (
            <ol className="settings__endless-results" aria-label="Recent endless mode results">
              {[...endless.results]
                .slice(-20)
                .reverse()
                .map((result, index) => (
                  <li
                    key={`${result.scenario}-${String(index)}`}
                    className={`mono settings__endless-result${
                      result.pass ? '' : ' settings__endless-result--fail'
                    }`}
                  >
                    {result.pass ? 'pass' : 'fail'} {result.scenario} ({result.durationMs}ms,{' '}
                    {result.reconnects} reconnects)
                    {result.reason !== undefined && <span> - {result.reason}</span>}
                  </li>
                ))}
            </ol>
          )}
        </div>
      </div>
    </section>
  );
}
